import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { partnerApi, type CommunicationRecipient } from '../../lib/api';

interface RecipientsTableProps {
  communicationGroupId: string;
}

export function RecipientsTable({
  communicationGroupId,
}: RecipientsTableProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 100;

  // Fetch recipients
  const { data, isLoading, error } = useQuery({
    queryKey: ['communication-recipients', communicationGroupId, page],
    queryFn: () =>
      partnerApi.getCommunicationRecipients(communicationGroupId, {
        page,
        limit,
      }),
  });

  const recipients = useMemo(() => data?.recipients || [], [data]);
  const pagination = data?.pagination;

  // Apply search filter on client side
  const filteredRecipients = useMemo(() => {
    if (!searchQuery.trim()) return recipients;

    const query = searchQuery.toLowerCase();
    return recipients.filter(recipient => {
      const fullName = [recipient.firstName, recipient.lastName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const email = recipient.email?.toLowerCase() || '';
      const phone = recipient.phone || '';

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        phone.includes(query)
      );
    });
  }, [recipients, searchQuery]);

  const getDisplayName = (recipient: CommunicationRecipient): string => {
    if (recipient.firstName || recipient.lastName) {
      return [recipient.firstName, recipient.lastName]
        .filter(Boolean)
        .join(' ');
    }
    return recipient.email || recipient.phone || 'Unknown';
  };

  const getContactMethod = (recipient: CommunicationRecipient): string => {
    if (recipient.email) return recipient.email;
    if (recipient.phone) return recipient.phone;
    return 'No contact info';
  };

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Error loading recipients:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[700px] bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center px-4 py-3 border-b border-gray-200">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">
            Recipients {pagination && `(${pagination.total})`}
          </h3>
        </div>
        <button
          onClick={() => setIsSearchOpen(!isSearchOpen)}
          className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          title="Search recipients">
          <Search className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Search Input */}
      {isSearchOpen && (
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-gray-400"
              autoFocus
            />
            <button
              onClick={() => {
                setIsSearchOpen(false);
                setSearchQuery('');
              }}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : filteredRecipients.length === 0 ? (
          <div className="text-center py-8 px-4">
            <p className="text-sm text-gray-500">
              {searchQuery.trim()
                ? `No recipients found matching "${searchQuery}"`
                : 'No recipients found'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredRecipients.map((recipient, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {getDisplayName(recipient)}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {getContactMethod(recipient)}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {recipient.delivered && (
                    <span
                      className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded"
                      title="Delivered">
                      Delivered
                    </span>
                  )}
                  {recipient.opened && (
                    <span
                      className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
                      title="Opened">
                      Opened
                    </span>
                  )}
                  {recipient.clicked && (
                    <span
                      className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded"
                      title="Clicked">
                      Clicked
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200">
          <div className="text-xs text-gray-600">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              Previous
            </button>
            <button
              onClick={() =>
                setPage(p => Math.min(pagination.totalPages, p + 1))
              }
              disabled={page === pagination.totalPages}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
